import { computed, ref } from 'vue'
import { useProjectsStore } from '@/stores/projects'
import type { Project, ProjectType } from '@/models/Project'

/**
 * MYAC-1327 — which project the CHAT is bound to, resolved in ONE place.
 *
 * Inside a project route the answer is the route's own pid — byte-identical to
 * what the router guards write into `projectsStore.currentProjectId`, so the
 * in-project chat cannot change behaviour. Everywhere else (the dashboard, the
 * lists) the chat resolves a scope from the account's own projects, by TIER.
 *
 * ## The tiers
 *
 * `SCOPE_TIERS` is a fallback chain, richest context first. The FIRST tier that
 * holds any project wins outright; the tiers below it are never offered:
 *
 * | The account holds | The chat |
 * |---|---|
 * | one QMS | opens on it |
 * | several QMS | asks which |
 * | no QMS, one Product | opens on it |
 * | no QMS, several Products | asks which |
 * | no QMS, no Product, one watch | opens on it |
 * | no QMS, no Product, several watches | asks which |
 * | nothing at all | offers to create a project |
 *
 * A QMS carries the links to its technical files, so a QMS-scoped session
 * already reaches the account's Products through the existing connector
 * (`parent_qms_project`; the agent crosses it via `resolve_scope`). That is why
 * a company WITH a QMS is never offered its Products here — the QMS is the
 * better scope, not a competing one. The lower tiers exist for accounts that
 * genuinely have no QMS: a Product-only account, or a watch-only (veille)
 * subscription, both of which are real plans.
 *
 * Deliberately NOT `projectsStore.currentProjectId`: that value is sticky (set
 * on entering a project route, never cleared on the way back out) and seeded
 * with the tenant's first project on a cold load — so the dashboard chat was
 * answering about whatever project the user last visited, or an arbitrary one.
 * The agent side refuses exactly that fallback (project_scope.py: no
 * `accessible_projects_ids[0]`); the front refuses it too.
 *
 * Modes:
 * - `project` — a project route: scope is the route's pid, header untouched.
 * - `scoped`  — global surface, resolved to one project (remembered, or the
 *               only one in the active tier).
 * - `pick`    — global surface, several in the active tier and no remembered
 *               pick: the user chooses in the header. Nothing is sent until
 *               they do — the chat refuses to answer about a project it has
 *               not been pointed at.
 * - `create`  — global surface, the account holds no projects at all: the chat
 *               offers to create one rather than pretending to have context.
 */

/** Fallback chain, richest context first (see the table above). */
export const SCOPE_TIERS = ['qms', 'td', 'regulatory_monitoring'] as const satisfies readonly ProjectType[]

export type ChatScopeMode = 'project' | 'scoped' | 'pick' | 'create'

// Module state, shared by every consumer (dock, full-page chat, chatbot store):
// the chat is one conversation, so its scope is one value.
//
// NOTHING here is persisted, deliberately. A stored preference outlived the
// conversation it belonged to and started answering "which of your projects?" on
// the member's behalf — on an account with 23 QMS the dashboard showed a fixture
// project nobody had picked, and never asked again. The scope now lives exactly as
// long as the conversation does.
const routeProjectId = ref<string | null>(null)

// The project the CURRENT conversation belongs to, pushed in by the panel (see
// `setLiveConversationProject`). This is what carries a scope back out of a
// project: chat inside one, walk to the dashboard, and the conversation — and the
// name in the header — come with you.
const liveProjectId = ref<string | null>(null)

// A pick made in the header before any message exists. Transient on purpose: it
// holds the scope just long enough to start a conversation, and is dropped on the
// way into a project, so coming back out asks again rather than resurrecting a
// choice with nothing behind it.
const pickedId = ref<string | null>(null)

// A project a reply of the welcome letter aimed the chat at (MYAC-1522): the reply
// speaks about that project's point, so its own chat answers it, even when the
// header's tier does not hold it (a Product with no QMS link, a watch beside a
// QMS). Offered beside the tier for exactly as long as the pick it came with.
const aimedId = ref<string | null>(null)

export function useChatScope() {
    const projectsStore = useProjectsStore()

    /** This account's projects (active tenant only — `tenantProjects` filters). */
    const tenantProjects = computed<Project[]>(() => projectsStore.tenantProjects ?? [])

    /** The first tier holding anything — the chat's scope comes from it alone. */
    const scopeTier = computed<ProjectType | null>(() =>
        SCOPE_TIERS.find((tier) => tenantProjects.value.some((p) => p.type === tier)) ?? null)

    /**
     * What the header offers, name-sorted.
     *
     * Sorted because the store is filled by a Firestore `getDocs`, which returns
     * DOCUMENT-ID order — meaningless to a human, and unusable once an account
     * holds more than a handful. `undefined` locale + base sensitivity is the
     * house pattern (ProjectCreateDialog.vue): it is accent-aware, and it dodges
     * the invalid-locale-tag `RangeError` that MYAC-1002 hit by passing the
     * active vue-i18n tag straight into `localeCompare`.
     *
     * A project a letter reply aimed at (`aimScope`) is offered beside the tier.
     */
    const scopeOptions = computed<Project[]>(() => {
        const tier = scopeTier.value
        if (!tier) return []
        return tenantProjects.value
            .filter((p) => p.type === tier || p.id === aimedId.value)
            .slice()
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }))
    })

    /** Only ids the header actually offers may become the scope. */
    const offered = (id: string | null) =>
        id && scopeOptions.value.some((p) => p.id === id) ? id : null

    /**
     * Global-surface resolution, in order:
     *
     * 1. the live conversation's project — a conversation started inside a project
     *    keeps its subject when the member walks out to the dashboard;
     * 2. a pick just made in the header, before that conversation exists;
     * 3. the only option, when the tier holds exactly one;
     * 4. nothing — and the header asks.
     *
     * Deliberately NO "first of several" default, and nothing remembered across
     * conversations: with several to choose from and no conversation in flight, the
     * honest state is the question.
     */
    const globalScopeId = computed(() =>
        offered(liveProjectId.value) ?? offered(pickedId.value)
        ?? (scopeOptions.value.length === 1 ? scopeOptions.value[0].id : null))

    // The project the route names, once the store knows it. Undefined while it
    // is still loading — treated as a real scoped chat below so a scoped chat
    // never flashes the onboarding during load.
    const routeProject = computed<Project | undefined>(() =>
        routeProjectId.value
            ? tenantProjects.value.find((p) => p.id === routeProjectId.value)
            : undefined)

    // A project route is a REAL scoped chat for any type the chat can scope to —
    // every tier, now that a watch project is a scope of its own. Unknown (still
    // loading) counts as scoped, so a real chat never flashes onboarding.
    const isScopedProjectRoute = computed<boolean>(() => {
        if (!routeProjectId.value) return false
        const p = routeProject.value
        if (!p) return true
        return (SCOPE_TIERS as readonly string[]).includes(p.type ?? '')
    })

    const scopeProjectId = computed<string | null>(() =>
        isScopedProjectRoute.value ? routeProjectId.value : globalScopeId.value)

    const scopeMode = computed<ChatScopeMode>(() => {
        if (isScopedProjectRoute.value) return 'project'
        if (globalScopeId.value) return 'scoped'
        return scopeOptions.value.length ? 'pick' : 'create'
    })

    const scopeProject = computed<Project | undefined>(() => {
        const id = scopeProjectId.value
        if (!id) return undefined
        return tenantProjects.value.find((p) => p.id === id)
    })

    /**
     * The chat has no project to answer about and one is available: refuse to
     * send until the user picks. `create` is deliberately NOT blocked — there is
     * nothing to pick, and a brand-new account must still be able to ask what the
     * platform is.
     */
    const isAwaitingPick = computed(() => scopeMode.value === 'pick')

    /**
     * The member chose this one in the header.
     *
     * A choice made while a conversation is on screen is a new subject, not a
     * preference behind it (MYAC-1522, Aymeric 2026-10-08: picking another project
     * during a conversation did nothing, because the live project outranks a pick).
     * So a different project releases the live one: the scope moves to the pick, and
     * the chat's scope watcher drops the conversation, as the header already promises
     * (e2e/scenarios/dashboard-chat-scope.md, step 11).
     */
    const selectScope = (id: string) => {
        if (!offered(id)) return
        if (liveProjectId.value && liveProjectId.value !== id) liveProjectId.value = null
        if (aimedId.value !== id) aimedId.value = null
        pickedId.value = id
    }

    /**
     * A reply of the welcome letter goes to the chat of the project it speaks about
     * (MYAC-1522): picked like a header choice, but any of the account's projects
     * the chat can scope to, not only the header's tier.
     */
    const aimScope = (id: string) => {
        const project = tenantProjects.value.find((p) => p.id === id)
        if (!project || !(SCOPE_TIERS as readonly string[]).includes(project.type ?? '')) return
        aimedId.value = project.type === scopeTier.value ? null : id
        selectScope(id)
    }

    /** Route wiring — GlobalChatPanel's navigation watcher is the one caller. */
    const setRouteProjectId = (pid: string | null) => {
        routeProjectId.value = pid
        // Walking into a project drops a pick that never became a conversation:
        // inside, the route is the scope, and on the way back out the header should
        // ask again rather than restore a choice with nothing behind it.
        if (pid) {
            pickedId.value = null
            aimedId.value = null
        }
    }

    /**
     * Which project the CURRENT conversation belongs to, or null when there is no
     * conversation. The panel pushes this in — the composable must not import the
     * chatbot store, which already imports this one.
     */
    const setLiveConversationProject = (pid: string | null) => { liveProjectId.value = pid }

    return {
        scopeProjectId, scopeMode, scopeProject, scopeOptions, scopeTier,
        isAwaitingPick, selectScope, aimScope, setRouteProjectId, setLiveConversationProject,
        routeProjectId,
    }
}

/**
 * Clear the module-level state between tests (PR #751 review).
 *
 * `routeProjectId` and the remembered picks deliberately live OUTSIDE Pinia —
 * the chat is one conversation, so its scope is one value shared by every
 * consumer — which means `setActivePinia(createPinia())` does not reset them.
 * Tests that exercise more than one scope must call this, or the previous
 * test's route/pick leaks into the next one.
 */
export function resetChatScopeForTests(): void {
    routeProjectId.value = null
    liveProjectId.value = null
    pickedId.value = null
    aimedId.value = null
}
